import { z } from 'zod';
import { AllCharLabelSchema, HumanFullNameSchema, UserTypeSchema } from '../common/schemas';
import { DBPermissionPermissionIdSchema, PermissionValueSchema } from '../permissions/schemas';
import { UserIdSchema } from '../users/schemas';
import { GroupPermissionType, PermissionGroupPrefix } from './enums';

/**
 * Shared & Helper Schemas
 */

export const DBPermissionGroupGenericPermissionsSchema = z.record(
  z.lazy(() => DBPermissionPermissionIdSchema),
  z.object({}).strict()
);

export const PermissionGroupGenericPermissionsSchema = z.array(z.lazy(() => PermissionValueSchema));

export const DBPermissionGroupUsersEntrySchema = z.object({
  name: HumanFullNameSchema,
  type: UserTypeSchema,
});

export const PermissionGroupUsersEntrySchema = z.object({
  name: HumanFullNameSchema,
  type: UserTypeSchema,
  userId: z.lazy(() => UserIdSchema),
});

export const DBGroupPermissionTypeSchema = z.enum(GroupPermissionType);

/**
 * DB Schemas
 */

export const DBPermissionGroupGroupIdSchema = z
  .string()
  .regex(/^[A-Z0-9_]+$/)
  .refine(val => Object.values(PermissionGroupPrefix).some(prefix => val.startsWith(prefix)))
  .min(1)
  .max(256);

export const DBPermissionGroupNameSchema = AllCharLabelSchema.min(1).max(256);
export const DBPermissionGroupBasePermissionsSchema = DBPermissionGroupGenericPermissionsSchema;
export const DBPermissionGroupCurrPermissionsSchema = DBPermissionGroupGenericPermissionsSchema;
export const DBPermissionGroupHideSchema = z.boolean();
export const DBPermissionGroupUsersSchema = z.record(
  z.lazy(() => UserIdSchema),
  DBPermissionGroupUsersEntrySchema
);

export const DBPermissionGroupSchema = z.object({
  groupId: DBPermissionGroupGroupIdSchema,
  // .optional() indicates to ElectroDB that keys of these dynamic fields may be deleted
  basePermissions: DBPermissionGroupBasePermissionsSchema.optional(),
  currPermissions: DBPermissionGroupCurrPermissionsSchema.optional(),
  hide: DBPermissionGroupHideSchema,
  name: DBPermissionGroupNameSchema,
  users: DBPermissionGroupUsersSchema.optional(),
});

/**
 * Client Schemas
 */

export const PermissionGroupGroupIdSchema = DBPermissionGroupGroupIdSchema;
export const PermissionGroupBasePermissionsSchema = PermissionGroupGenericPermissionsSchema;
export const PermissionGroupCurrPermissionsSchema = PermissionGroupGenericPermissionsSchema;
export const PermissionGroupHideSchema = DBPermissionGroupHideSchema;
export const PermissionGroupNameSchema = DBPermissionGroupNameSchema;
export const PermissionGroupUsersSchema = z.array(PermissionGroupUsersEntrySchema);

export const PermissionGroupSchema = z.object({
  groupId: PermissionGroupGroupIdSchema,
  basePermissions: PermissionGroupBasePermissionsSchema.default([]),
  currPermissions: PermissionGroupCurrPermissionsSchema.default([]),
  hide: PermissionGroupHideSchema.default(false),
  name: PermissionGroupNameSchema,
  users: PermissionGroupUsersSchema.default([]),
});
